/**
 * Detail simples do canal WhatsApp Não Oficial.
 * Mostra status da sessão + ações (pausar/reativar agente, excluir, reconectar).
 *
 * Pausa do agente: chama set_agent_paused (backend remove agent_id/agent_flow_id
 * do metadata da sessão mas mantém a sessão online). Mensagens continuam
 * chegando na Central de Atendimento — apenas as respostas automáticas
 * ficam desligadas.
 */

import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconBrandWhatsapp,
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
  IconAlertTriangle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import useWahaStatus from './useStatus';
import {
  SetAgentPaused,
  DeleteAccount,
} from '../../../../../services/WaBottyServices';

const WahaDetail = ({ agentId, navigation }) => {
  const status = useWahaStatus(agentId);
  const [busy, setBusy] = useState(false);

  if (status.isLoading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (!status.raw) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <IconBrandWhatsapp size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
        <p style={{ color: '#6b7280' }}>Nenhuma sessão do whatsapp vinculada a este agente.</p>
        <button
          type="button"
          onClick={() => navigation?.switchToChannel?.('whatsapp_waha')}
          style={{
            marginTop: 12,
            padding: '10px 20px',
            background: '#25D366',
            border: 'none',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Conectar agora
        </button>
      </div>
    );
  }

  const session = status.raw;
  const agentPaused = !!status.agentPaused;

  // Pausa "soft": apenas remove o agente do metadata da sessão (backend
  // set_agent_paused). Mensagens continuam chegando à Central de Atendimento;
  // só as respostas automáticas ficam desligadas. A sessão NÃO é parada.
  const handleTogglePause = async () => {
    // Telefone numérico limpo (extraído do me.id pelo hook).
    const phone = status.phone;
    if (!phone || !/^\d+$/.test(phone)) {
      Swal.fire({
        icon: 'error',
        title: 'Não foi possível identificar o número',
        text: 'Atualize a lista e tente novamente.',
      });
      return;
    }

    const willPause = !agentPaused;
    const confirmation = await Swal.fire({
      title: willPause ? 'Pausar agente?' : 'Reativar agente?',
      icon: 'warning',
      html: willPause
        ? `<div style="text-align:left;font-size:13px;line-height:1.5">
             <p><strong>Ao pausar o agente:</strong></p>
             <ul style="margin:8px 0 8px 20px;padding:0">
               <li>O agente <strong>não responderá</strong> automaticamente</li>
               <li>As mensagens dos clientes <strong>continuarão chegando</strong>
                   à Central de Atendimento normalmente</li>
               <li>Você pode reativar a qualquer momento</li>
             </ul>
             <p style="color:#6b7280;margin-top:8px">
               Útil para responder manualmente em horários específicos
               sem desconectar o número.
             </p>
           </div>`
        : `<div style="text-align:left;font-size:13px;line-height:1.5">
             <p>O agente voltará a responder mensagens automaticamente.</p>
           </div>`,
      showCancelButton: true,
      confirmButtonColor: willPause ? '#d97706' : '#16a34a',
      confirmButtonText: willPause ? 'Pausar agente' : 'Reativar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmation.isConfirmed) return;

    setBusy(true);
    try {
      await SetAgentPaused({ phone, paused: willPause });
      Swal.fire({
        icon: 'success',
        title: willPause ? 'Agente pausado' : 'Agente reativado',
        text: willPause
          ? 'A Central de Atendimento continua recebendo mensagens normalmente.'
          : 'O agente voltará a responder mensagens automaticamente.',
        timer: 2200,
        showConfirmButton: false,
      });
      status.refresh();
    } catch (err) {
      const apiMsg =
        err?.data?.error
        || err?.data?.details
        || (typeof err?.data === 'string' ? err.data : null)
        || 'Tente novamente em alguns instantes.';
      Swal.fire({
        icon: 'error',
        title: willPause ? 'Erro ao pausar agente' : 'Erro ao reativar agente',
        text: apiMsg,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const confirm = await Swal.fire({
      title: 'Excluir sessão?',
      text: 'Você está prestes a excluir a conexão do whatsapp do agente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    try {
      const payload = { session_name: session.name };
      if (session.phone) payload.phone = session.phone;
      await DeleteAccount(payload);
      Swal.fire({
        icon: 'success',
        title: 'Sessão excluída',
        timer: 1500,
        showConfirmButton: false,
      });
      navigation?.goBackToGrid?.();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Tente novamente.' });
    } finally {
      setBusy(false);
    }
  };

  const needsQR = session.status === 'SCAN_QR_CODE' || session.status === 'FAILED';

  return (
    <div>
      <div className="cloudapi-hero">
        <div
          className="cloudapi-hero__avatar"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
        >
          <IconBrandWhatsapp size={28} />
        </div>
        <div className="cloudapi-hero__info">
          <h3 className="cloudapi-hero__phone">{status.phone || session.name}</h3>
          <p className="cloudapi-hero__business">
            {session.me?.pushName || 'WhatsApp Não Oficial '}
          </p>
          <div className="cloudapi-hero__pills">
            <span className={`channel-card__status-pill channel-card__status-pill--${status.statusVariant}`}>
              {status.statusLabel}
            </span>
          </div>
        </div>
        <div className="cloudapi-hero__actions">
          <button
            type="button"
            onClick={() => status.refresh()}
            className="cloudapi-hero__action-btn"
          >
            <IconRefresh size={14} />
            Atualizar
          </button>
        </div>
      </div>

      {needsQR && (
        <div
          style={{
            padding: 12,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            marginBottom: 12,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            fontSize: 13,
            color: '#92400e',
          }}
        >
          <IconAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>Sessão precisa de QR Code.</strong> Use o fluxo de
            reconexão para escanear o QR Code novamente.
          </div>
        </div>
      )}

      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">Controle do agente</h4>

        {agentPaused && (
          <div
            style={{
              padding: 10,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              marginBottom: 10,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#92400e',
            }}
          >
            <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Agente pausado.</strong> O agente não está respondendo
              automaticamente, mas as mensagens dos clientes continuam
              chegando à Central de Atendimento normalmente.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleTogglePause}
            disabled={busy}
            className="cloudapi-hero__action-btn"
          >
            {agentPaused ? <IconPlayerPlay size={14} /> : <IconPlayerPause size={14} />}
            {agentPaused ? 'Reativar agente' : 'Pausar agente'}
          </button>
        </div>

        <p style={{ fontSize: 11, color: '#6b7280', margin: '8px 0 0' }}>
          {agentPaused
            ? 'O agente voltará a responder mensagens automaticamente após reativar.'
            : 'Pausa apenas as respostas automáticas. As mensagens dos clientes seguem chegando à Central de Atendimento para atendimento humano.'}
        </p>
      </div>

      <div
        className="cloudapi-section"
        style={{ borderColor: '#fecaca', background: '#fef2f2' }}
      >
        <h4 className="cloudapi-section__title" style={{ color: '#991b1b' }}>
          <IconAlertTriangle size={16} />
          Zona de Perigo
        </h4>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          className="cloudapi-hero__action-btn cloudapi-hero__action-btn--danger"
        >
          <IconTrash size={14} />
          Excluir sessão
        </button>
      </div>
    </div>
  );
};

export default WahaDetail;
